import { useAtomValue } from 'jotai';

import { useGetApiV1IndividualpaymentPeople } from '@/shared/api/generated';

import { individualPaymentAppliedFiltersAtom } from '../store/atoms/individualPaymentFiltersAtom';
import { buildPeopleQueryParams } from '../utils/buildQueryParams';
import { parsePeopleResponse } from '../utils/parsePeopleResponse';

/** Itens por página da listagem de alunos. */
export const INDIVIDUAL_PAYMENT_PAGE_SIZE = 20;

/**
 * Listagem de alunos (linhas-pai) da pesquisa de Pagamento Individual.
 *
 * Só dispara a requisição depois que o operador aplica os filtros — enquanto
 * `individualPaymentAppliedFiltersAtom` for `null`, a query fica desabilitada.
 *
 * A resposta passa por `parsePeopleResponse`: o Kubb tipa o corpo como array,
 * mas o backend pode envelopar em `{ items, totalCount, ... }`. Quando o
 * envelope traz os totais, eles são usados; caso contrário `hasNextPage` é
 * derivado do tamanho da página (resposta cheia ⇒ pode haver mais adiante).
 *
 * @param page - Página 1-based (default 1).
 * @param pageSize - Itens por página (default {@link INDIVIDUAL_PAYMENT_PAGE_SIZE}).
 *
 * @example
 * ```tsx
 * const { people, hasSearched, hasNextPage } = useIndividualPaymentPeople(pageIndex + 1);
 * ```
 */
export function useIndividualPaymentPeople(page = 1, pageSize = INDIVIDUAL_PAYMENT_PAGE_SIZE) {
  const applied = useAtomValue(individualPaymentAppliedFiltersAtom);
  const hasSearched = applied !== null;

  const { data, isLoading, isFetching, isError, refetch } = useGetApiV1IndividualpaymentPeople(
    applied !== null ? buildPeopleQueryParams(applied, page, pageSize) : undefined,
    { query: { enabled: hasSearched } },
  );

  const { people, totalCount, totalPages } = hasSearched
    ? parsePeopleResponse(data, pageSize)
    : { people: [], totalCount: null, totalPages: null };

  const offset = (page - 1) * pageSize;

  return {
    people,
    isLoading: hasSearched && isLoading,
    isFetching,
    isError,
    refetch,
    hasSearched,
    totalCount,
    totalPages,
    hasNextPage: totalPages !== null ? page < totalPages : people.length === pageSize,
    hasPreviousPage: page > 1,
    rangeFrom: people.length === 0 ? 0 : offset + 1,
    rangeTo: offset + people.length,
    pageSize,
  } as const;
}
